import api from '../utils/axios';

const API_ENDPOINTS = {
  EMPLOYEES: '/employee',
  EMPLOYEES_BY_DEPARTMENTS: '/employee/by-departments',
  EMPLOYEE_AUTH: '/employee-auth',
  COMPANIES: '/companies',
  DEPARTMENTS: '/departments',
  BRANCHES: '/branches'
};

export const employeeService = {
  // Get all employees (company-specific)
  async getAllEmployees() {
    try {
      console.log('🔍 Fetching employees from:', API_ENDPOINTS.EMPLOYEES);
      const response = await api.get(API_ENDPOINTS.EMPLOYEES);
      console.log('📊 Employees API response:', response);
      console.log('📊 Response data structure:', response.data);
      
      // Handle different response structures
      let employeesData;
      if (response.data.data && Array.isArray(response.data.data)) {
        employeesData = response.data.data;
      } else if (Array.isArray(response.data)) {
        employeesData = response.data;
      } else {
        employeesData = [];
      }
      
      return { 
        success: true, 
        data: employeesData 
      };
    } catch (error) {
      console.error('Error fetching employees:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to fetch employees' 
      };
    }
  },

  // Get employees by departments (company-specific with department filtering)
  async getEmployeesByDepartments() {
    try {
      console.log('🔍 Fetching employees by departments from:', API_ENDPOINTS.EMPLOYEES_BY_DEPARTMENTS);
      
      // Check if token exists
      const companyToken = localStorage.getItem('companyToken');
      const employeeToken = localStorage.getItem('employeeToken');
      const authToken = localStorage.getItem('authToken');
      const token = companyToken || employeeToken || authToken;
      
      console.log('🔑 Token available:', !!token);
      console.log('🔑 Company token:', !!companyToken);
      
      const response = await api.get(API_ENDPOINTS.EMPLOYEES_BY_DEPARTMENTS);
      console.log('📊 Employees by departments API response:', response.data);
      console.log('📊 Response status:', response.status);
      
      // Check if we got unauthorized response
      if (response.status === 401) {
        console.log('❌ Unauthorized - token issue');
        return { 
          success: false, 
          error: 'Authentication failed' 
        };
      }
      
      return { 
        success: true, 
        data: response.data.data || [],
        departments: response.data.departments || [],
        employeesByDepartment: response.data.employeesByDepartment || [],
        totalEmployees: response.data.totalEmployees || 0,
        totalDepartments: response.data.totalDepartments || 0
      };
    } catch (error) {
      console.error('Error fetching employees by departments:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to fetch employees by departments' 
      };
    }
  },

  // Get employees by company (from local backend)
  async getEmployeesByCompany(companyId) {
    try {
      const response = await api.get(`${API_ENDPOINTS.EMPLOYEES}/company/${companyId}`);
      return { 
        success: true, 
        data: response.data.data || response.data || [] 
      };
    } catch (error) {
      console.error('Error fetching company employees from local backend:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to fetch company employees' 
      };
    }
  },

  // Create employee (using local backend)
  async createEmployee(employeeData) {
    try {
      console.log('🆕 Creating employee via local backend:', employeeData);
      const response = await api.post(API_ENDPOINTS.EMPLOYEES, employeeData);
      console.log('✅ Employee created:', response.data);
      
      return { 
        success: true, 
        data: response.data.data || response.data 
      };
    } catch (error) {
      console.error('Error creating employee via local backend:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to create employee' 
      };
    }
  },

  // Update employee (using local backend)
  async updateEmployee(employeeId, employeeData) {
    try {
      console.log('📝 Updating employee via local backend:', employeeId, employeeData);
      const response = await api.put(`${API_ENDPOINTS.EMPLOYEES}/${employeeId}`, employeeData);
      console.log('✅ Employee updated:', response.data);
      
      return { 
        success: true, 
        data: response.data.data || response.data 
      };
    } catch (error) {
      console.error('Error updating employee via local backend:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to update employee' 
      };
    }
  },

  // Delete employee (using local backend)
  async deleteEmployee(employeeId) {
    try {
      console.log('🗑️ Deleting employee via local backend:', employeeId);
      const response = await api.delete(`${API_ENDPOINTS.EMPLOYEES}/${employeeId}`);
      console.log('✅ Employee deleted:', response.data);
      
      return { 
        success: true, 
        message: response.data.message || 'Employee deleted successfully' 
      };
    } catch (error) {
      console.error('Error deleting employee via local backend:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to delete employee' 
      };
    }
  },

  // Get employee by ID (using local backend)
  async getEmployeeById(employeeId) {
    try {
      const response = await api.get(`${API_ENDPOINTS.EMPLOYEES}/${employeeId}`);
      return { 
        success: true, 
        data: response.data.data || response.data 
      };
    } catch (error) {
      console.error('Error fetching employee via local backend:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to fetch employee' 
      };
    }
  },

  // Create sales team (bulk create via local backend)
  async createSalesTeam() {
    try {
      console.log('👥 Creating sales team via local backend...');
      const response = await api.post(`${API_ENDPOINTS.EMPLOYEES}/create-sales-team`);
      console.log('✅ Sales team created:', response.data);
      
      return { 
        success: true, 
        data: response.data.data || response.data,
        message: response.data.message 
      };
    } catch (error) {
      console.error('Error creating sales team via local backend:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to create sales team' 
      };
    }
  },

  // Employee Login
  async login(email, password) {
    try {
      console.log('🔐 Employee login attempt:', email);
      const response = await api.post(`${API_ENDPOINTS.EMPLOYEE_AUTH}/login`, {
        email,
        password
      });
      
      if (response.data.success && response.data.token) {
        // Store employee token
        localStorage.setItem('employeeToken', response.data.token);
        localStorage.setItem('employeeData', JSON.stringify(response.data.employee));
        
        console.log('✅ Employee login successful');
        return {
          success: true,
          data: response.data.employee,
          token: response.data.token
        };
      }
      
      return {
        success: false,
        error: 'Login failed'
      };
    } catch (error) {
      console.error('Error in employee login:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed. Please check your credentials.'
      };
    }
  },

  // Get current employee profile
  async getCurrentEmployee() {
    try {
      const response = await api.get(`${API_ENDPOINTS.EMPLOYEE_AUTH}/me`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error fetching employee profile:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch profile'
      };
    }
  },

  // Logout employee
  logout() {
    localStorage.removeItem('employeeToken');
    localStorage.removeItem('employeeData');
    console.log('✅ Employee logged out');
  }
};

export default employeeService;
